import { HttpClient } from "@angular/common/http";
import { Injector } from "@angular/core";
import { GridActionHandlerBase } from "../grid-base/actions/grid-action-handler-base";
import { GridBaseComponent } from "../grid-base/grid-base";
import { DestroyBase } from "../ng-destroy-base";
import { TabConfig } from "../pollux-tab-config/pollux-form-config";
import { WorkplaceBase } from "../workplace-base/workplace-base";
import { AppServiceHelperService } from "../../../services/app-service-helper.service";
import { FileDownloadService } from "../../../services/file-download-service.service";
import { EventBusService } from "../../../services/event-bus.service";
import { DataCollection } from "@progress/kendo-angular-grid/data/data.collection";

/**
 * Workspace base class. A workspace coordinates a grid and a form for a business object and interacts with the workplace
 */
export abstract class WorkspaceBase extends DestroyBase {
  /**
   * The grid
   */
  private _grid: GridBaseComponent = null;

  /**
   * Gets the action handlers used by this workspace area. Implement all logic in these handlers, NOT in the derived class of this class.
   */
  protected abstract get actionHandlers(): GridActionHandlerBase[];

  /**
   * Gets the GridBase component
   */
  public get grid(): GridBaseComponent {
    return this._grid;
  }

  /**
   * Sets the GridBase component. Must be triggered once the grid component is created. !!! The grid component must not be hidden by using *ngIf !!!
   */
  public set grid(value: GridBaseComponent) {
    this._grid = value;
  }

  /**
   * The show grid flag
   */
  private _showGrid = true;

  /**
   * Gets the show grid flag
   */
  public get showGrid(): boolean {
    return this._showGrid;
  }

  /**
   * Sets the show grid flag
   */
  public set showGrid(value: boolean) {
    if (this._showGrid !== value) {
      this._showGrid = value;
      this.onShowGridChanged(value);
    }
  }

  /**
   * Sets the workplace
   */
  public abstract set workplace(workplace: WorkplaceBase);

  /**
   * Gets the workplace
   */
  public abstract get workplace(): WorkplaceBase;

  /**
   * Sets the tab element
   */
  public abstract set tabElement(tabElement: TabConfig);

  /**
   * Gets the tab element
   */
  public abstract get tabElement(): TabConfig;

  /**
   * Oid of BO opened in workspace form
   */
  public oid: number = null;

  /**
   * JavaScript Oid property name of BO
   */
  public abstract oidPropertyName: string;

  /**
   * The event bus
   */
  protected eventBus: EventBusService;

  /**
   * The Angular HttpClient
   */
  protected http: HttpClient;

  /**
   * AppServiceHelperService
   */
  protected appServiceHelper: AppServiceHelperService;

  /**
   * File download service
   */
  protected fileDownloadService: FileDownloadService;

  /**
   * Constructor
   * @param injector The Angular Injector
   */
  constructor(protected injector: Injector) {
    super();
    this.eventBus = injector.get(EventBusService);
    this.http = injector.get(HttpClient);
    this.appServiceHelper = injector.get(AppServiceHelperService);
    this.fileDownloadService = injector.get(FileDownloadService);
  }

  /**
   * Gets the selected items' oids. Assumes that the grid has an item named 'checkbox'
   */
  protected getCheckedItems(): any[] {
    return this.grid.getFlattenGridDataResultData().filter(item => item.checkbox).map(item => item[this.oidPropertyName]);
  }

  /**
   * Triggers the API-side export
   * @param areaName The area
   */
  public doExport(areaName: any): void {
    const oids = this.getCheckedItems();

    if (oids.length === 0) {
      this.eventBus.broadcastEvent("openConfirmDialog", {
        success: () => {
          //Nothing
        }
      }, 'noExportNothingSelected');
      return;
    }
    const url = this.appServiceHelper.getServiceEndpoint('pollux.import.api', 'export') + '?form=' + areaName;
    this.fileDownloadService.initiateDownloadPost(url, oids);
  }

  /**
   * Gets the grid data
   */
  public get kendoGridView(): DataCollection {
    return this.grid.kendoGrid.view;
  }

  /**
   * Shows the form
   * @param oid Oid of the element that should be opened. Use -1 for new mode
   */
  public abstract showForm(oid: number): void;

  /**
   * Called if the grid state was changed. Use this to notify the nav bar or perform similar actions
   * @param gridState The new grid state: true: Grid is shown, so the form isn't showh. False: Grid isn't show, but the form is shown
   */
  protected abstract onShowGridChanged(gridState: boolean): void;

  /**
   * Registers any required events. Please don't forget to unsubscibe!
   */
  protected abstract registerEvents(): void;

  /**
   * Applies a filter if an element was selected in a higher hierarchy area
   * @param item Item
   * @param selectedHierarchy Hierarchy where the element was selected
   * @param tabConfig Tab config
   * @param reset Reset the selection in the current area
   */
  public abstract filter(item: any, selectedHierarchy: number, tabConfig: any, reset: boolean): void;

  /**
   * Initializes the form
   * @returns A Promise that should be awaited
   */
  protected init(): Promise<void> {
    this.registerEvents();
    this.registerActionHandlers(this.actionHandlers);
    return new Promise<void>((resolve) => {
      resolve();
    });
  }

  /**
   * Should button Previous be displayed
   */
  public get previousButtonEnabled(): boolean {
    const items = this.grid.currentPageItems;
    return items.length && items[0][this.oidPropertyName] !== this.oid;
  }

  /**
   * Should button Next be displayed
   */
  public get nextButtonEnabled(): boolean {
    const items = this.grid.currentPageItems;
    return items.length && items[items.length - 1][this.oidPropertyName] !== this.oid;
  }

  /**
   * Show previous BO in form
   */
  public showPrevious(): void {
    const previousOid = this.getOidAtOffset(this.oidPropertyName, -1);
    this.oid = null;
    setTimeout(() => { //Intended
      this.oid = previousOid;
    });
  }

  /**
   * Show next BO in form
   */
  public showNext(): void {
    const nextOid = this.getOidAtOffset(this.oidPropertyName, 1);
    this.oid = null;
    setTimeout(() => { //Intended
      this.oid = nextOid;
    });
  }

  /**
   * Return Oid value at offset of this.oid
   * @param oidColumnName Oid column name
   * @param offset Offset
   * @returns Oid value at offset of this.oid
   */
  private getOidAtOffset(oidColumnName: string, offset: number): number {
    const oidsArray = this.grid.currentPageItems.map(row => row[oidColumnName]);
    const index = oidsArray.indexOf(this.oid) + offset;
    return oidsArray[index];
  }

  /**
   * Set counter all handler: Tell the workplace
   * @param area The area
   * @param amount The amount
   */
  public setCounterValue(area: string, amount: number): void {
    if (this.workplace) {
      this.workplace.setCounterValue(area, amount);
    }
    else {
      console.error('Invalid setup: workplace is null or undefined!');
    }
  }

  /**
   * Show all handler: Tell the workplace
   */
  public onShowAll(): void {
    if (this.workplace) {
      this.workplace.onShowAll(this.tabElement);
    }
    else {
      console.error('Invalid setup: workplace is null or undefined!');
    }
  }

  /**
   * Element selection handler: Tell the workplace
   * @param item The selected item (can be null)
   */
  public onElementSelected(item: any): void {
    if (this.workplace) {
      this.workplace.onElementSelected(this.tabElement, item, true);
    }
    else {
      console.error('Invalid setup: workplace is null or undefined!');
    }
  }

  /**
   * Element deselection handler: Tell the workplace
   */
  public onElementDeselected(): void {
    if (this.workplace) {
      this.workplace.onElementDeselected(this.tabElement);
    }
    else {
      console.error('Invalid setup: workplace is null or undefined!');
    }
  }

  /**
   * Registers an array of GridActionHandlerBase instances. These keep the workplace logic
   * @param handlers The handlers
   */
  private registerActionHandlers(handlers: GridActionHandlerBase[]): void {
    if (handlers?.length) {
      for (const handler of handlers) {
        this.registerActionHandler(handler);
      }
    }

    this.onEvents.push(this.eventBus.subscribeEvent('ActiveBusinessUnitChanged', () => {
      this.showGrid = true;
      this.grid.refreshGridClicked();
    }));
  }

  /**
  * Registers a single GridActionHandlerBase instance. These keep the workplace logic
  * @param handlers The handlers
  */
  private registerActionHandler(handler: GridActionHandlerBase): void {
    if (handler?.topic) {
      this.onEvents.push(this.eventBus.subscribeEvent(handler.topic, (payload: any, _options) => {
        handler.handle(payload);
      }));
    }
    else {
      console.error('Invalid setup: handler or it\'s topic  is null or undefined!');
    }
  }
}
